import { addDays, differenceInCalendarDays } from "date-fns"
import { formatDateOnly, parseDateOnly } from "@/lib/datetime"
import type { TcmContactState, TcmEpisode, TcmVisitState } from "../types"

/**
 * What the 30-day clock says, in words: the billing zones, the 2-day contact deadline, today and the
 * visit, each with a tooltip (title, body, an optional action) and a tone. Everything is derived from
 * the episode the board already sends; graham computes contact_state and visit_state, with a local
 * fallback for an older API.
 *
 * Visual rules (Darshak, 2026-10-23): red = act now, amber = today, green = done, an outline =
 * scheduled, gray = not yet due. Every state also has a symbol, so the clock reads in grayscale.
 */

export type MarkerTone = "red" | "amber" | "green" | "outline" | "gray"
export type MarkerSymbol = "x" | "check" | "bang" | "ring" | "dot"

/** A case-panel action a tooltip can start (TcmCaseDrawer's forms). */
export type ClockAction = "contact" | "attempt" | "done" | "book"

export interface ClockTipInfo {
  title: string
  body: string
  action?: { label: string; mode: ClockAction }
}

export interface ClockMarker extends ClockTipInfo {
  tone: MarkerTone
  symbol: MarkerSymbol
  /** Day on the 0-29 clock where the marker sits. */
  day: number
}

export interface ClockZone extends ClockTipInfo {
  id: "z1" | "z2" | "z3"
  from: number
  to: number
  /** The ruler label: "0–7 · 99496 or 99495". */
  ruler: string
}

const md = (d: Date | string | null | undefined) => formatDateOnly(d ?? null, "EEE M/d")

/** Clock positions as a percent of the 30-day period (days 0..29 plus the end). */
export function dayPercent(day: number): number {
  return Math.max(0, Math.min(100, (day / 30) * 100))
}

export const ZONE_RULER = [
  { id: "z1" as const, from: 0, to: 7, ruler: "0–7 · 99496 or 99495" },
  { id: "z2" as const, from: 8, to: 14, ruler: "8–14 · 99495 only" },
  { id: "z3" as const, from: 15, to: 29, ruler: "15–29 · not TCM-billable" },
]

/** Zone widths on the bar: day 0 to the end of day 7, to the end of day 14, to the end. */
export function zoneSpan(zone: { from: number; to: number }): { left: number; width: number } {
  const left = zone.from === 0 ? 0 : dayPercent(zone.from)
  const right = zone.to === 29 ? 100 : dayPercent(zone.to + 1)
  return { left, width: right - left }
}

function start(ep: TcmEpisode): Date | null {
  return parseDateOnly(ep.clock?.start ?? null)
}

export function contactStateOf(ep: TcmEpisode, today: string): TcmContactState {
  if (ep.clock?.contact_state) return ep.clock.contact_state
  const due = ep.clock?.contact_due
  if (ep.contact_status === "DONE") return due && ep.contact_date && ep.contact_date > due ? "late" : "reached"
  if (ep.contact_status === "ATTEMPTED_2X") {
    return due && ep.contact_attempts.filter((a) => a.date && a.date <= due).length < 2 ? "late" : "attempted"
  }
  return due && today > due ? "missed" : "due"
}

export function visitStateOf(ep: TcmEpisode, today: string): TcmVisitState {
  if (ep.clock?.visit_state) return ep.clock.visit_state
  if (ep.f2f_completed_date) return "done"
  if (!ep.f2f_scheduled_date) return "none"
  if (ep.f2f_scheduled_date === today) return "today"
  return ep.f2f_scheduled_date < today ? "passed" : "booked"
}

export function zones(ep: TcmEpisode): ClockZone[] {
  const s = start(ep)
  if (!s) return []
  return [
    {
      ...ZONE_RULER[0],
      title: "99496 or 99495 window",
      body: `Through ${md(addDays(s, 7))} (day 7). A visit here can bill either code; 99496 needs high-complexity decisions.`,
    },
    {
      ...ZONE_RULER[1],
      title: "99495 window",
      body: `${md(addDays(s, 8))} to ${md(addDays(s, 14))} (days 8–14). A visit here can bill 99495 only.`,
    },
    {
      ...ZONE_RULER[2],
      title: "Not TCM-billable",
      body: `From ${md(addDays(s, 15))} (day 15). Still see the patient; bill the visit as an office visit.`,
    },
  ]
}

function by(name: string | null | undefined): string {
  return name ? ` by ${name}` : ""
}

export function contactMarker(ep: TcmEpisode, today: string): ClockMarker | null {
  const c = ep.clock
  const s = start(ep)
  if (!c || !s) return null
  const day = differenceInCalendarDays(parseDateOnly(c.contact_due) as Date, s)
  const state = contactStateOf(ep, today)
  const due = md(c.contact_due)
  switch (state) {
    case "reached":
      return { day, tone: "green", symbol: "check", title: "Contact made",
        body: `Reached ${md(ep.contact_date)}${by(ep.contact_by)}.` }
    case "attempted": {
      const dates = ep.contact_attempts.map((a) => md(a.date)).join(" and ")
      return { day, tone: "green", symbol: "check", title: "2 attempts logged",
        body: `${dates}. Counts as the 2-day contact; keep trying to reach the patient.` }
    }
    case "late":
      return { day, tone: "amber", symbol: "bang", title: "Contact after the deadline",
        body: ep.contact_status === "DONE"
          ? `Reached ${md(ep.contact_date)}, after the ${due} deadline. Not TCM-billable; bill the visit as an office visit.`
          : `Fewer than 2 attempts by ${due}. Not TCM-billable; bill the visit as an office visit.` }
    case "missed":
      return { day, tone: "red", symbol: "x", title: "2-day contact missed",
        body: `Was due ${due}. If the patient was reached (or 2 attempts made) by then, log it; otherwise TCM can't be billed. Still see the patient.`,
        action: { label: "Log contact", mode: "contact" } }
    default:
      return { day, tone: c.contact_due === today ? "amber" : "gray", symbol: c.contact_due === today ? "bang" : "dot",
        title: "2-day contact due",
        body: `Call by ${due}. Reaching the patient, or 2 documented attempts, counts.`,
        action: { label: "Log attempt", mode: "attempt" } }
  }
}

export function visitMarker(ep: TcmEpisode, today: string, ehr = "EHR"): ClockMarker | null {
  const c = ep.clock
  if (!c || c.visit_day === null) return null
  const day = c.visit_day
  const date = ep.f2f_completed_date ?? ep.f2f_scheduled_date
  const withDr = ep.f2f_provider ? ` with ${ep.f2f_provider}` : ""
  const note = ep.ehr_status === "filed" ? `in ${ehr}` : ep.note ? `not in ${ehr} yet` : "not ready yet"
  switch (visitStateOf(ep, today)) {
    case "done": {
      const codes = c.allowed_codes.length ? `Codes allowed: ${c.allowed_codes.join(", ")}.` : "Not TCM-billable; bill as an office visit."
      return { day, tone: "green", symbol: "check", title: `Seen day ${day}`, body: `${md(date)}${withDr}. ${codes}` }
    }
    case "today":
      return { day, tone: "amber", symbol: "bang", title: "Visit today",
        body: `${ep.f2f_provider ? `With ${ep.f2f_provider}. ` : ""}Note: ${note}.`,
        action: { label: "Mark visit done", mode: "done" } }
    case "passed":
      return { day, tone: "red", symbol: "x", title: "Visit date passed",
        body: `Was ${md(date)} (day ${day}). Mark it done or rebook it.`,
        action: { label: "Mark visit done", mode: "done" } }
    default:
      return { day, tone: "outline", symbol: "ring", title: "Visit booked",
        body: `${md(date)} (day ${day})${withDr}. Paste the note in before the visit (note ${note}).` }
  }
}

export function todayInfo(ep: TcmEpisode): ClockTipInfo & { day: number } {
  const c = ep.clock
  const day = c?.day ?? 0
  const left = (last: number) => last - day
  const body =
    day <= 7 ? `${left(7)} day${left(7) === 1 ? "" : "s"} left in the 99496 window (99495 until day 14).`
      : day <= 14 ? `${left(14)} day${left(14) === 1 ? "" : "s"} left in the 99495 window.`
        : day <= 29 ? "Past the TCM visit window. The 30-day period ends on day 29."
          : "The 30-day period is over."
  return { day: Math.min(day, 29.6), title: `Today · day ${day} of 30`, body }
}

/** One plain line under the bar: "Contact missed · Seen day 6 ✓". */
export function statusWords(ep: TcmEpisode, today: string): string {
  const c = ep.clock
  if (!c) return ""
  const contact = {
    due: `Contact due ${md(c.contact_due)}`,
    missed: "Contact missed",
    reached: "Contact made",
    attempted: "2 attempts logged",
    late: "Contact late",
  }[contactStateOf(ep, today)]
  const visit = {
    none: "No visit booked",
    booked: `Visit day ${c.visit_day} · ${md(ep.f2f_scheduled_date)}`,
    today: "Visit today",
    passed: `Visit day ${c.visit_day} · not marked done`,
    done: `Seen day ${c.visit_day} ✓`,
  }[visitStateOf(ep, today)]
  return `Day ${c.day} · ${contact} · ${visit}`
}

/** The bar as one sentence for screen readers: "Day 8 of 30. Contact missed. Seen on day 6." */
export function barSentence(ep: TcmEpisode, today: string): string {
  const c = ep.clock
  if (!c) return ""
  const contact = {
    due: `Contact due ${md(c.contact_due)}.`,
    missed: "Contact missed.",
    reached: "Contact made.",
    attempted: "Two contact attempts logged.",
    late: "Contact made after the deadline.",
  }[contactStateOf(ep, today)]
  const visit = {
    none: "No visit booked.",
    booked: `Visit booked for day ${c.visit_day}.`,
    today: "Visit today.",
    passed: `Visit on day ${c.visit_day} not marked done.`,
    done: `Seen on day ${c.visit_day}.`,
  }[visitStateOf(ep, today)]
  return `Day ${c.day} of 30. ${contact} ${visit}`
}
